import { actions as appActions } from 'Data/ReduxViewModels/App';
import { hasValue } from 'Lib/Any';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button';
import { IDialogProps } from './types';

/**
 * A static counter to track how many dialogs we have mounted so we can assign each a unique id
 */
let dialogCounter = 0;

/**
 * Provides a modal dialog
 */
const DialogBase: React.FC<React.PropsWithChildren<IDialogProps>> = ({
  buttons,
  children,
  onClose,
  title,
  width,
  minHeight,
  visible,
  className,
  autoFocusRef,
}) => {
  const dispatch = useDispatch();
  const [dialogCount] = useState(dialogCounter++);
  const id = `dialog_${dialogCount}`;

  const dialogRef = useRef<HTMLDivElement | null>(null);

  const isLatestActiveDialog = useSelector(
    (state) => state.app.activeDialogs.length > 0 && state.app.activeDialogs[state.app.activeDialogs.length - 1] === id
  );

  // Register/unregister this dialog as active when it becomes visible/invisible
  useEffect(() => {
    if (visible) dispatch(appActions.setDialogActive({ dialogId: id }));

    return () => {
      if (visible) dispatch(appActions.setDialogInactive({ dialogId: id }));
    };
  }, [dispatch, visible, id]);

  useHotkeys(
    'escape',
    (event) => {
      if (!event.defaultPrevented) {
        event.preventDefault();
        onClose?.(event);
      }
    },
    { enabled: visible && isLatestActiveDialog && hasValue(onClose), enableOnFormTags: ['INPUT', 'SELECT', 'TEXTAREA'] }
  );

  /**
   * Reference to the element in buttons to auto focus on 9as a fallback if autoFocusRef is not provided)
   */
  const buttonAutoFocusRef = useRef<any>(null);

  if (!visible) return null;

  // Inject buttonAutoFocusRef to first button with autoFocus attribute (unless autoFocusRef is provided)
  if (!hasValue(autoFocusRef)) {
    let autoFocusButton = buttons?.find((e) => e?.props['autoFocus']) as any;
    if (autoFocusButton)
      buttons!.splice(
        buttons!.indexOf(autoFocusButton),
        1,
        React.cloneElement(autoFocusButton, { focusRef: buttonAutoFocusRef })
      );
  }

  // Create a portal to attach the dialog to the root of the DOM tree
  return createPortal(
    <FocusTrap
      focusTrapOptions={{
        // Dropdown menus count as "outside" so we need to allow outside clicks
        allowOutsideClick: true,
        clickOutsideDeactivates: false,
        // We use our own hotkey hook for closing this dialog on escape so we don't want the focus trap to intercept that
        escapeDeactivates: false,
        // Set the initial focus element
        // The behaviour when this is omitted can have undesirable effects when no element has an autoFocus attribute
        // (e.g. focussing on something unintended that can look strange to touch/mouse users)
        // so we should provide _something_ here to explicitly set where the initial focus is.
        // We first check the autoFocusRef prop (used by consumers to set this directly).
        // If that is not provided, we default to the first button with the property autoFocus.
        // If that is not provided, we focus on the dialog as a whole.
        initialFocus: () => autoFocusRef?.current ?? buttonAutoFocusRef.current ?? dialogRef.current!,
        // Focus trap will throw an error if the trap doesn't contain a tabbable element (e.g. dialog has no buttons or fields)
        // and we do not provide a valid fallback element
        fallbackFocus: () => dialogRef.current!,
      }}
    >
      <div className="Dialog-Backdrop">
        <div
          className={classNames('Dialog', className ?? '')}
          role="dialog"
          style={{ width, minHeight }}
          ref={dialogRef}
          /**
           * Setting tabIndex to -1 means the dialog can have its focus set programmatically (by the focus trap)
           * but cannot be navigated to by the keyboard
           */
          tabIndex={-1}
        >
          <div className="Dialog-Header">
            <div className="Dialog-Title">{title}</div>
            {hasValue(onClose) && (
              <div className="Dialog-HeaderButtons">
                <Button key={id} subtle onClick={onClose} light alwaysEnabled>
                  <MdClose />
                </Button>
              </div>
            )}
          </div>
          <div className="Dialog-Content">{children}</div>
          {buttons && <div className="Dialog-ButtonGroup">{buttons}</div>}
        </div>
      </div>
    </FocusTrap>,
    document.body
  );
};

export { DialogBase };
export default DialogBase;
